import React, {
    HTMLAttributes,
    MouseEvent,
    ReactNode,
    ReactPortal,
} from 'react'
import { createPortal } from 'react-dom'

import closeIconSVG from 'assets/images/close-icon.svg'
import { twMerge } from 'tailwind-merge'
import { Typography } from '../Typography'

export interface BottomSheetProps extends HTMLAttributes<HTMLDivElement> {
    readonly children: ReactNode
    readonly title?: string
    readonly isOpen: boolean
    readonly draggable?: boolean
    readonly close: () => void
    readonly overlay?: boolean
    readonly hideOnBlur?: boolean
    readonly showCloseButton?: boolean
}

function stopPropagation(e: MouseEvent<HTMLDivElement>): void {
    e.stopPropagation()
}

const BottomSheet = ({
    children,
    isOpen,
    title,
    draggable,
    close,
    overlay,
    hideOnBlur,
    showCloseButton,
    className,
    ...props
}: BottomSheetProps): ReactPortal | null => {
    const containerEle = document.getElementById('bottom-sheet-root')

    if (!containerEle) return null

    return createPortal(
        <>
            {isOpen ? (
                <div
                    {...props}
                    className={twMerge(
                        'justify-center items-center',
                        className
                    )}
                    onClick={close}
                >
                    {/* Overlay */}
                    <div
                        className={twMerge(
                            'fixed flex top-0 bottom-0 left-0 right-0',
                            overlay ? 'bg-black/[.35]' : 'bg-transparent',
                            hideOnBlur ? '' : 'pointer-events-none'
                        )}
                    />
                    {/* Modal Body */}
                    <div
                        onClick={stopPropagation}
                        className={twMerge(
                            'flex flex-col absolute bottom-0 w-[80vw] bg-white p-4',
                            draggable ? 'pt-1' : '',
                            'shadow-lg rounded-lg',
                            'sm:w-screen sm:min-h-[auto]',
                            showCloseButton
                                ? 'sm:max-h-[calc(100dvh_-_2rem_-_2.5rem)]'
                                : 'sm:max-h-[100dvh]',
                            'sm:rounded-b-none sm:rounded-t-lg'
                        )}
                    >
                        {showCloseButton && (
                            <div
                                onClick={close}
                                className="bg-white absolute flex items-center justify-center -top-14 right-4 w-10 h-10 drop-shadow-md rounded-full"
                            >
                                <img
                                    src={closeIconSVG}
                                    width={14}
                                    height={14}
                                />
                            </div>
                        )}
                        {draggable && (
                            <hr
                                style={{
                                    width: '20%',
                                    margin: '12px auto',
                                    border: '3px solid #e8e8e8',
                                    borderRadius: '1em',
                                }}
                            ></hr>
                        )}
                        {title && (
                            <div className="flex relative mt-2 mb-4">
                                <Typography.Title
                                    variant="moderate"
                                    weight="font-semibold"
                                >
                                    {title}
                                </Typography.Title>
                            </div>
                        )}
                        <div className="overflow-auto">{children}</div>
                    </div>
                </div>
            ) : null}
        </>,
        containerEle
    )
}

export default BottomSheet
